import { useCallback, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { EarnAction, IAddress } from '~/types'
import { getVaultAsset, isVaultToken } from '~/utils/vault'

export function useClearSwapData() {
  const { setValue, getValues } = useFormContext<TransferFormValues>()

  return useCallback(() => {
    const currentSlippage = getValues('swapData.slippage')
    setValue('swapData', {
      slippage: currentSlippage,
    })
  }, [setValue, getValues])
}

export function useAutoSwapSelection() {
  const { setValue } = useFormContext<TransferFormValues>()
  const clearSwapData = useClearSwapData()

  const onSelectAutoSwap = useCallback(
    (token: { address: IAddress; chainId: number } | undefined) => {
      if (token) {
        setValue('swapData.sellToken.token', token.address)
        setValue('swapData.sellToken.chainId', token.chainId)
        setValue('swapData.sellToken.amount', '0')
        setValue('calls', [])
        if (isVaultToken(token.chainId, token.address)) {
          const assetToken = getVaultAsset(token.chainId, token.address)
          setValue('swapData.earnToken.token', assetToken)
          setValue('swapData.earnToken.chainId', token.chainId)
          setValue('swapData.earnToken.amount', '0')
          setValue('swapData.earnToken.action', EarnAction.WITHDRAW)
        } else {
          setValue('swapData.earnToken', undefined)
        }
      } else {
        clearSwapData()
      }
    },
    [setValue, clearSwapData],
  )

  return { onSelectAutoSwap, clearSwapData }
}

export function useSelectedSwapToken() {
  const { control } = useFormContext<TransferFormValues>()
  const swapData = useWatch({ control, name: 'swapData' })

  return useMemo(() => {
    if (!swapData?.sellToken) return undefined
    return {
      address: swapData.sellToken.token,
      chainId: swapData.sellToken.chainId,
      amount: swapData.sellToken.amount,
    }
  }, [swapData])
}
